import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Input, Pagination, Table, Empty, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    CheckCircle,
    XCircle,
    FilePdf,
    FileText,
    House,
    Link as LinkIcon,
    MagnifyingGlass,
    ArrowsLeftRight,
} from '@phosphor-icons/react';
import DashboardModal from './DashboardModal';
import HoverLogoMark from './HoverLogoMark';
import { RoofProposalDetailModal } from './RoofProposalDetailModal';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { roofTextureList } from '../../../../Constants';
import hoverServices, {
    getHoverMeasurementsPdfUrl,
    getHoverPropertyDetailsUrl,
    getSfHoverJobUrl,
    mapHoverEstimateDto,
    type HoverRoofEstimate,
} from '../../../../services/oneButtonServices/hoverServices';
import './oneButton-css/HoverRoofEstimatesModal.css';

export type HoverRoofEstimatesModalProps = {
    open: boolean;
    onClose: () => void;
    opportunityId: string | number | null | undefined;
    /** Called with the selected estimate (Angular Convert — no convert API). */
    onConvert: (estimate: HoverRoofEstimate) => void;
};

function resolveShingleImage(productCode: string | null): string | null {
    if (!productCode) return null;
    const code = String(productCode).trim();
    const matched = roofTextureList.find(
        (t) => String(t.name).toLowerCase() === code.toLowerCase(),
    );
    return matched?.img ?? null;
}

function formatCurrency(n: number): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number.isFinite(n) ? n : 0);
}

function formatSq(n: number): string {
    return `${Number(n || 0).toFixed(2)} sq`;
}

function formatEstimateDate(raw: string): string {
    if (!raw) return '';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });
}

const PAGE_SIZE = 10;

const HoverRoofEstimatesModal: React.FC<HoverRoofEstimatesModalProps> = ({
    open,
    onClose,
    opportunityId,
    onConvert,
}) => {
    const theme = useOneButtonStore((s) => s.theme);
    const storeHoverJobId = useOneButtonStore((s) => s.hoverJobId);

    const [loading, setLoading] = useState(false);
    const [estimates, setEstimates] = useState<HoverRoofEstimate[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [sfHoverId, setSfHoverId] = useState('');
    const [listHoverJobId, setListHoverJobId] = useState('');
    const [proposalOpen, setProposalOpen] = useState(false);

    const loadEstimates = useCallback(async () => {
        if (opportunityId == null || String(opportunityId).trim() === '') {
            setEstimates([]);
            return;
        }
        setLoading(true);
        try {
            const res = await hoverServices.getHoverJobEstimates(opportunityId);
            const rows = Array.isArray(res?.HoverEstimateDTO) ? res!.HoverEstimateDTO! : [];
            setSfHoverId(res?.HoverJobSFID ? String(res.HoverJobSFID) : '');
            const mapped = rows.map((item) => mapHoverEstimateDto(item, resolveShingleImage));
            setEstimates(mapped);
            if (mapped.length > 0) {
                setListHoverJobId(mapped[0].jobId.replace(/^#/, ''));
            } else {
                setListHoverJobId('');
            }
        } catch {
            setEstimates([]);
            setSfHoverId('');
            setListHoverJobId('');
        } finally {
            setLoading(false);
        }
    }, [opportunityId]);

    useEffect(() => {
        if (!open) {
            setProposalOpen(false);
            return;
        }
        setSearchQuery('');
        setCurrentPage(1);
        void loadEstimates();
    }, [open, loadEstimates]);

    const filteredEstimates = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return estimates;
        return estimates.filter((e) => e.jobId.toLowerCase().includes(q));
    }, [estimates, searchQuery]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    const paginatedEstimates = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return filteredEstimates.slice(start, start + PAGE_SIZE);
    }, [filteredEstimates, currentPage]);

    const effectiveHoverJobId =
        listHoverJobId ||
        (storeHoverJobId ? String(storeHoverJobId).replace(/^#/, '') : '');

    const sfHoverJobUrl = getSfHoverJobUrl();

    const handleConvert = (estimate: HoverRoofEstimate) => {
        onConvert(estimate);
        onClose();
    };

    const columns: ColumnsType<HoverRoofEstimate> = [
        {
            title: 'Job ID',
            key: 'jobId',
            width: '19%',
            render: (_, row) => (
                <div>
                    <div className="obd-hover-estimates__job-id">{row.jobId}</div>
                    {row.date1 ? (
                        <div className="obd-hover-estimates__date">{formatEstimateDate(row.date1)}</div>
                    ) : null}
                </div>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            width: '20%',
            render: (_, row) => (
                <div>
                    <div
                        className={`obd-hover-estimates__status${
                            row.status === 'Converted' ? ' obd-hover-estimates__status--converted' : ''
                        }`}
                    >
                        {row.status === 'Converted' ? (
                            <CheckCircle size={14} weight="bold" aria-hidden />
                        ) : (
                            <XCircle size={14} weight="bold" aria-hidden />
                        )}
                        {row.status}
                    </div>
                    <div
                        className={`obd-hover-estimates__status-label${
                            row.status === 'Converted' ? ' obd-hover-estimates__status-label--quote' : ''
                        }`}
                    >
                        {row.statusLabel}
                    </div>
                </div>
            ),
        },
        {
            title: 'Details',
            key: 'details',
            width: '18%',
            render: (_, row) => (
                <div className="obd-hover-estimates__details">
                    <div>
                        Low: <strong>{formatSq(row.lowSq)}</strong>
                    </div>
                    <div>
                        Normal: <strong>{formatSq(row.normalSq)}</strong>
                    </div>
                </div>
            ),
        },
        {
            title: 'Estimate',
            key: 'pricing',
            width: '14%',
            render: (_, row) => (
                <div className="obd-hover-estimates__pricing">{formatCurrency(row.pricing)}</div>
            ),
        },
        {
            title: 'Shingle',
            key: 'shingle',
            width: '15%',
            render: (_, row) => (
                <div className="obd-hover-estimates__shingle">
                    {row.shingleImage ? (
                        <img src={row.shingleImage} alt="" className="obd-hover-estimates__shingle-img" />
                    ) : (
                        <div className="obd-hover-estimates__shingle-placeholder" aria-hidden />
                    )}
                    <div className="obd-hover-estimates__shingle-name">{row.shingleType || '—'}</div>
                </div>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: '14%',
            render: (_, row) => (
                <Button
                    type="default"
                    className="obd-hover-estimates__convert"
                    onClick={() => handleConvert(row)}
                    icon={<ArrowsLeftRight size={14} weight="bold" aria-hidden />}
                >
                    Convert
                </Button>
            ),
        },
    ];

    const showingFrom =
        filteredEstimates.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const showingTo = Math.min(currentPage * PAGE_SIZE, filteredEstimates.length);

    return (
        <>
        <DashboardModal
            open={open}
            onClose={onClose}
            title={
                <span className="obd-hover-logo-title">
                    <span className="obd-hover-logo-title__text">Roof Estimates</span>
                    <HoverLogoMark size="md" />
                </span>
            }
            theme={theme}
            width={900}
            maskClosable={false}
            destroyOnClose
            loading={loading}
            loadingMessage="Loading roof estimates…"
            className="obd-hover-estimates-modal"
            rootClassName="obd-hover-estimates-modal-root"
        >
            <div className="obd-hover-estimates">
                <div className="obd-hover-estimates__intro">
                    <p className="obd-hover-estimates__subtitle">
                        View all roof estimates. Customize an estimate to generate a quote.
                    </p>
                    <button
                        type="button"
                        className="obd-hover-estimates__proposal"
                        title="Roof Proposal Details"
                        aria-label="Roof Proposal Details"
                        onClick={() => setProposalOpen(true)}
                    >
                        <FileText size={16} weight="bold" aria-hidden />
                    </button>
                </div>

                <div className="obd-hover-estimates__toolbar">
                    <Input
                        allowClear
                        variant="borderless"
                        className="obd-hover-estimates__search"
                        placeholder="Search by Job ID..."
                        prefix={<MagnifyingGlass size={14} weight="bold" aria-hidden />}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <div className="obd-hover-estimates__links">
                        {effectiveHoverJobId ? (
                            <a
                                className="obd-hover-estimates__link obd-hover-estimates__link--property"
                                href={getHoverPropertyDetailsUrl(effectiveHoverJobId)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <House size={14} weight="regular" aria-hidden />
                                View Property Details
                            </a>
                        ) : null}
                        {effectiveHoverJobId ? (
                            <a
                                className="obd-hover-estimates__link obd-hover-estimates__link--pdf"
                                href={getHoverMeasurementsPdfUrl(effectiveHoverJobId)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FilePdf size={14} weight="regular" aria-hidden />
                                Hover Measurements PDF
                            </a>
                        ) : null}
                        {sfHoverId ? (
                            <a
                                className="obd-hover-estimates__link obd-hover-estimates__link--sf"
                                href={`${sfHoverJobUrl}${sfHoverId}/view`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <LinkIcon size={14} weight="bold" aria-hidden />
                                SF Hover Link
                            </a>
                        ) : null}
                    </div>
                </div>

                <Table
                    className="obd-hover-estimates__table"
                    columns={columns}
                    dataSource={paginatedEstimates}
                    rowKey={(row) => `${row.jobId}-${row.hoverEstimateID}-${row.date1}`}
                    pagination={false}
                    size="middle"
                    scroll={{ y: 300 }}
                    locale={{
                        emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Data" />,
                    }}
                />

                {filteredEstimates.length > 0 ? (
                    <div className="obd-hover-estimates__footer">
                        <span className="obd-hover-estimates__showing">
                            Showing {showingFrom} to {showingTo} of {filteredEstimates.length}
                        </span>
                        {filteredEstimates.length > PAGE_SIZE ? (
                            <Pagination
                                size="small"
                                current={currentPage}
                                pageSize={PAGE_SIZE}
                                total={filteredEstimates.length}
                                onChange={(page) => setCurrentPage(page)}
                                showSizeChanger={false}
                            />
                        ) : null}
                    </div>
                ) : null}
            </div>
        </DashboardModal>
        <RoofProposalDetailModal
            open={proposalOpen}
            onClose={() => setProposalOpen(false)}
            zIndex={13000}
        />
        </>
    );
};

export default HoverRoofEstimatesModal;
